import { addMonths, endOfMonth, startOfMonth, subDays } from 'date-fns';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  DateRangePicker,
  Modal,
  Panel,
  SelectPicker,
  Toggle,
} from 'rsuite';
import { useSelector, useDispatch } from 'react-redux';
import { Flex } from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/apiHelpers';
import {
  setDefaultDateValue,
  setPeriodData,
  setPeriodFrom,
  setPeriodSelect,
  setPeriodTo,
  setPeriodSwitcher,
  setLastFetchedIntegration,
  setCompanySwitcherActive,
} from '../../redux/slices/dateSlice';

const CustomDateRangePicker = ({ dateValue }) => {
  // Redux state
  const dispatch = useDispatch();
  const dateFormat = useSelector(
    state => state.dateFormat?.value || 'MM-dd-yyyy'
  );
  const periodFrom = useSelector(state => state.dateFormat?.periodFrom || '');
  const periodTo = useSelector(state => state.dateFormat?.periodTo || '');
  const periodSelect = useSelector(
    state => state.dateFormat?.periodSelect || false
  );
  const periodData = useSelector(state => state.dateFormat?.periodData || []);
  const periodSwitcher = useSelector(
    state => state.dateFormat?.periodSwitcher || false
  );
  const dataLoading = useSelector(
    state => state.dataFetch?.dataLoading || false
  );
  const dataFetch = useSelector(state => state.dataFetch?.dataFetch || false);
  const defaultDateValue = useSelector(
    state => state.dateFormat?.defaultDateValue
  );
  const integration = useSelector(
    state => state.locationSelectFormat?.integration || null
  );
  const companySwitcherActive = useSelector(
    state => state.dateFormat?.companySwitcherActive || false
  );
  const periodDataFetched = useSelector(
    state => state.dateFormat?.periodDataFetched || false
  );
  const lastFetchedIntegration = useSelector(
    state => state.dateFormat?.lastFetchedIntegration || null
  );

  // Local state
  const [localState, setLocalState] = useState({
    period: false,
    periodFrom: periodFrom,
    periodTo: periodTo,
    openModal: false,
    defaultSwitcher: false,
    periodData: [],
  });

  // Fetch period data
  const fetchPeriod = useCallback(() => {
    // Skip if we've already fetched data for this integration
    if (periodDataFetched && integration === lastFetchedIntegration) {
      return; // No cleanup function needed
    }

    const url =
      '/api/fetch_periods/' + (integration !== null ? integration : 'null');

    const controller = new AbortController();
    const signal = controller.signal;

    // Update the last fetched integration
    dispatch(setLastFetchedIntegration(integration));

    fetchWithAuth(url, {
      method: 'GET',
      signal,
    })
      .then(response => response.json())
      .then(data => {
        if (data.code === undefined) {
          if (data.period_cal === 'true') {
            const dataNew = data.period_data.map(item => ({
              label: item.period_label,
              value: item.period_label,
            }));

            setLocalState(prev => ({
              ...prev,
              periodData: dataNew,
            }));

            dispatch(setPeriodData(dataNew)); // This will also set periodDataFetched to true
            dispatch(setPeriodSwitcher(true)); // Enable period switcher when periods are available

            if (periodFrom === '') {
              setLocalState(prev => ({
                ...prev,
                periodFrom: data.from_period,
                periodTo: data.to_period,
              }));

              dispatch(setPeriodFrom(data.from_period));
              dispatch(setPeriodTo(data.to_period));
            }
          } else {
            dispatch(setPeriodSwitcher(false));
            dispatch(setPeriodFrom(''));
            dispatch(setPeriodTo(''));
          }

          // Set company period calendar status - required for period calendar to show
          dispatch(
            setCompanySwitcherActive(data.company_period_cal === 'true')
          );
        } else {
          window.open('/', '_self');
          alert('Session Expired!.');
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Error fetching period data:', err);
        }
      });

    return () => controller.abort();
  }, [
    integration,
    periodFrom,
    dispatch,
    periodDataFetched,
    lastFetchedIntegration,
  ]);

  // Convert date for storage
  const storeDateConverter = useCallback(value => {
    const val = [];
    if (value !== undefined) {
      value.forEach(date => {
        val.push(date.toISOString());
      });
    }
    return val;
  }, []);

  // Convert date for calendar props
  const calendarPropsDateConverter = useCallback(value => {
    const val = [];
    if (value !== undefined) {
      value.forEach(item => {
        val.push(new Date(new Date(item).toString()));
      });
    }
    return val;
  }, []);

  // Generate period label
  const periodLabel = useCallback(() => {
    let from = '';
    let to = '';

    if (periodFrom !== '' || periodTo !== '') {
      from = periodFrom;
      to = periodTo;
    } else if (localState.defaultSwitcher) {
      // Only fetch if we haven't already fetched for this integration
      if (!periodDataFetched || integration !== lastFetchedIntegration) {
        fetchPeriod();
      }
      from = periodFrom;
      to = periodTo;
    }

    return from + ' ~ ' + to;
  }, [
    periodFrom,
    periodTo,
    localState.defaultSwitcher,
    fetchPeriod,
    periodDataFetched,
    integration,
    lastFetchedIntegration,
  ]);

  // Check if switcher should be shown
  const checkSwitcherCondition = useCallback(() => {
    return (
      (periodSwitcher || localState.defaultSwitcher) && companySwitcherActive
    );
  }, [periodSwitcher, localState.defaultSwitcher, companySwitcherActive]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setLocalState(prev => ({
      ...prev,
      openModal: !prev.openModal,
    }));
  }, []);

  // Predefined date ranges
  const predefinedBottomRanges = useMemo(
    () => [
      {
        label: 'Last 30 days',
        value: [subDays(new Date(), 29), new Date()],
        closeOverlay: false,
      },
      {
        label: 'Last month',
        value: [
          startOfMonth(addMonths(new Date(), -1)),
          endOfMonth(addMonths(new Date(), -1)),
        ],
        closeOverlay: false,
      },
      {
        label: 'All time',
        value: [subDays(new Date(), 365), new Date()],
        closeOverlay: false,
      },
    ],
    []
  );

  // Update local state when props change
  useEffect(() => {
    setLocalState(prev => ({
      ...prev,
      periodFrom: periodFrom,
      periodTo: periodTo,
    }));
  }, [periodFrom, periodTo]);

  // Fetch period data when integration changes
  useEffect(() => {
    fetchPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // fetchPeriod is intentionally excluded to prevent double execution
    // since it's a useCallback that depends on integration
  }, [integration]);

  // Format date to MM-DD-YYYY for API
  const formatDateForAPI = useCallback(dateObj => {
    if (!dateObj) return '';
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${month}-${day}-${year}`;
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback(
    value => {
      // First update the Redux state
      dispatch(setDefaultDateValue(storeDateConverter(value)));

      // Then immediately call dateValue without setTimeout
      // Pass the date values to the parent component
      if (typeof dateValue === 'function') {
        // Format dates in MM-DD-YYYY format as expected by the backend
        const fromDate = formatDateForAPI(value[0]);
        const toDate = formatDateForAPI(value[1]);

        dateValue(value, fromDate, toDate);
      } else {
        // Otherwise call it as before
        dateValue();
      }
    },
    [dispatch, storeDateConverter, dateValue, formatDateForAPI]
  );

  // Handle period toggle change
  const handlePeriodToggleChange = useCallback(
    value => {
      dispatch(setPeriodSelect(value));
      // Call dateValue immediately without setTimeout
      // Pass the period values to the parent component
      if (typeof dateValue === 'function') {
        // If dateValue is a function that accepts parameters, pass the values
        dateValue(undefined, undefined, undefined, periodFrom, periodTo);
      } else {
        // Otherwise call it as before
        dateValue();
      }
    },
    [dispatch, dateValue, periodFrom, periodTo]
  );

  // Handle period selection
  const handlePeriodSelection = useCallback(() => {
    handleClose();

    // Update Redux state
    dispatch(setPeriodFrom(localState.periodFrom));
    dispatch(setPeriodTo(localState.periodTo));

    // Call dateValue immediately without setTimeout
    // Pass the period values to the parent component
    if (typeof dateValue === 'function') {
      // If dateValue is a function that accepts parameters, pass the values
      dateValue(
        undefined,
        undefined,
        undefined,
        localState.periodFrom,
        localState.periodTo
      );
    } else {
      // Otherwise call it as before
      dateValue();
    }
  }, [
    dispatch,
    localState.periodFrom,
    localState.periodTo,
    handleClose,
    dateValue,
  ]);

  // Handle period button click
  const handlePeriodButtonClick = useCallback(() => {
    handleClose();
    setLocalState(prev => ({
      ...prev,
      periodFrom: periodFrom,
      periodTo: periodTo,
    }));
  }, [handleClose, periodFrom, periodTo]);

  // Memoize date picker value
  const datePickerValue = useMemo(() => {
    return defaultDateValue !== undefined
      ? calendarPropsDateConverter(defaultDateValue)
      : [subDays(new Date(), 365), new Date()];
  }, [defaultDateValue, calendarPropsDateConverter]);

  return (
    <>
      <Modal open={localState.openModal} onClose={handleClose} backdrop={false}>
        <Modal.Body>
          <Flex direction={'row'}>
            <Panel bordered header="From">
              <SelectPicker
                placeholder="Period"
                id="periodFrom"
                style={{ width: 224 }}
                onChange={value => {
                  setLocalState(prev => ({ ...prev, periodFrom: value }));
                }}
                data={periodData}
                value={localState.periodFrom}
                onClean={() => {
                  setLocalState(prev => ({ ...prev, periodFrom: '' }));
                }}
                onSelect={value =>
                  setLocalState(prev => ({ ...prev, periodFrom: value }))
                }
              />
            </Panel>
            <Panel bordered header="To">
              <SelectPicker
                placeholder="Period"
                id="periodTo"
                style={{ width: 224 }}
                onChange={value => {
                  setLocalState(prev => ({ ...prev, periodTo: value }));
                }}
                data={periodData}
                value={localState.periodTo}
                onClean={() => {
                  setLocalState(prev => ({ ...prev, periodTo: '' }));
                }}
                onSelect={value =>
                  setLocalState(prev => ({ ...prev, periodTo: value }))
                }
              />
            </Panel>
          </Flex>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handlePeriodSelection} appearance="primary">
            Ok
          </Button>
          <Button onClick={handleClose} appearance="subtle">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      {checkSwitcherCondition() ? (
        <Flex
          direction={'row'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={2}
          className="date-picker-flex-container"
        >
          <Toggle
            checkedChildren="Period"
            unCheckedChildren="Period"
            onChange={handlePeriodToggleChange}
            checked={periodSelect}
            disabled={false}
            className="period-toggle"
          />

          {periodSelect ? (
            <>
              <Button
                size="sm"
                onClick={handlePeriodButtonClick}
                style={{ width: '100%', marginBlockEnd: 0 }}
                loading={!periodDataFetched}
                className="period-button"
              >
                {periodLabel()}
              </Button>
            </>
          ) : (
            <div
              className="date-picker-wrapper"
              onScroll={e => e.stopPropagation()}
            >
              <DateRangePicker
                loading={false}
                appearance="default"
                cleanable={false}
                placeholder="Date Range"
                placement={'bottomStart'}
                style={{ width: '100%', maxWidth: '100%' }}
                block
                size="sm"
                showOneCalendar
                format={dateFormat}
                ranges={predefinedBottomRanges}
                preventOverflow={false}
                container={() => document.body}
                onChange={handleDateRangeChange}
                value={datePickerValue}
                editable={false}
                defaultValue={datePickerValue}
              />
            </div>
          )}
        </Flex>
      ) : (
        <>
          <div
            className="date-picker-wrapper"
            onScroll={e => e.stopPropagation()}
          >
            <DateRangePicker
              loading={false}
              appearance="default"
              cleanable={false}
              placeholder="Date Range"
              placement={'bottomStart'}
              style={{ width: '100%', maxWidth: '100%' }}
              block
              size="sm"
              showOneCalendar
              format={dateFormat}
              ranges={predefinedBottomRanges}
              preventOverflow={false}
              container={() => document.body}
              onChange={handleDateRangeChange}
              value={datePickerValue}
              editable={false}
              defaultValue={datePickerValue}
            />
          </div>
        </>
      )}
    </>
  );
};

export default CustomDateRangePicker;
